import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import { cn, formatDate } from 'lib/utils';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useMyPlan, usePlanSeats } from 'features/subscription/hooks/use-seats';
import { seatPriceLabel } from 'features/subscription/utils/seat-pools';
import { BillingDetailsSection } from 'features/subscription/components/billing-overview';
import { isIncompleteCheckout } from 'features/subscription/utils/resolve-plan-select-action';
import {
  PlanActions,
  planStateLabel,
  planUnavailableNote,
  seatsLabel,
} from 'features/iam/components/seats/plan-actions';

interface SubscriptionTabV2Props {
    tabId: string;
    onCloseModal?: () => void;
}

const badgeClass = 'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold';

/**
 * Every Klax plan is user-wise. Everyone sees the seat they hold; an admin also sees each plan the
 * organization bought, and can finish an unpaid one, cancel one for everyone on it, or keep one.
 */
export const SubscriptionTabV2 = ({ onCloseModal }: SubscriptionTabV2Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isOnlyOrgUser = useCommonStore((state) => state.isOnlyOrgUser);
    const { place, isLoading: isPlaceLoading } = useMyPlan();
    const { planSeats, isLoading: isPlansLoading } = usePlanSeats();
    const bought = planSeats.filter((entry) => entry.pool);
    const isAdmin = !isOnlyOrgUser;

    const goToMembers = () => {
        onCloseModal?.();
        navigate('/identity-management');
    };

    if (isPlaceLoading || isPlansLoading) {
        return (
            <div className="h-full overflow-auto bg-background">
                <div className="max-w-4xl animate-pulse space-y-4 px-4 py-4 sm:px-6 sm:py-5 md:px-8">
                    <div className="h-6 w-40 rounded bg-muted" />
                    <div className="h-4 w-64 rounded bg-muted" />
                    <div className="h-16 w-full rounded bg-muted" />
                </div>
            </div>
        );
    }

    const pending = isIncompleteCheckout(place?.status);

    return (
        <div className="h-full overflow-auto bg-background">
            <div className="max-w-4xl space-y-6 px-4 py-4 sm:space-y-8 sm:px-6 sm:py-5 md:px-8">
                <div>
                    <h2 className="mb-1 text-lg font-semibold text-foreground sm:text-xl">
                        {t('SUBSCRIPTION', 'Subscription')}
                    </h2>
                    <p className="text-xs text-muted-foreground sm:text-sm">
                        {t('SUBSCRIPTION_TAB_DESCRIPTION', 'Manage your current plan, billing, and cancellation.')}
                    </p>
                </div>

                <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground sm:text-base">
                        {t('YOUR_PLAN', 'Your plan')}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-foreground sm:text-base">
                            {place ? place.planName || place.planCode : t('NO_PLAN', 'No plan')}
                        </h3>
                        <span
                            className={cn(
                                badgeClass,
                                !place
                                    ? 'border-border bg-muted/50 text-muted-foreground'
                                    : pending
                                        ? 'border-warning/30 bg-warning-background text-warning-high-emphasis'
                                        : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300'
                            )}
                        >
                            {!place
                                ? t('INACTIVE', 'Inactive')
                                : pending
                                    ? t('PAYMENT_PENDING', 'Payment pending')
                                    : t('ACTIVE', 'Active')}
                        </span>
                    </div>
                    <p className="text-xs text-muted-foreground sm:text-sm">
                        {!place
                            ? isAdmin
                                ? t('NO_SEAT_ADMIN_HINT', 'You hold no seat. Give yourself a plan from the members page.')
                                : t('NO_SEAT_MEMBER_HINT', 'You hold no seat. Ask your admin for a plan.')
                            : place.cancelAtPeriodEnd
                                ? t('SEAT_ENDS_ON', { date: formatDate(place.currentPeriodEndUtc), defaultValue: 'Ends on {{date}}.' })
                                : !pending &&
                                  t('SUBSCRIPTION_AUTO_RENEW', {
                                      date: formatDate(place.currentPeriodEndUtc),
                                      defaultValue: 'Your subscription will auto renew on {{date}}.',
                                  })}
                    </p>
                </div>

                {isAdmin && (
                    <>
                        <Separator className="h-px bg-border" />
                        <div className="space-y-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-sm font-medium text-muted-foreground sm:text-base">
                                    {t('ORGANIZATION_PLANS', 'Plans in your organization')}
                                </p>
                                <Button variant="outline" size="sm" onClick={goToMembers}>
                                    {t('MANAGE_SEATS', 'Manage seats')}
                                </Button>
                            </div>
                            {bought.length === 0 && (
                                <p className="text-xs text-muted-foreground sm:text-sm">
                                    {t('NO_PLANS_BOUGHT', 'No plans bought yet. Buy seats from the members page.')}
                                </p>
                            )}
                            {bought.map((entry) => {
                                const price = seatPriceLabel(t, entry, true);
                                const note = planUnavailableNote(t, entry);
                                return (
                                    <div
                                        key={entry.code}
                                        className="flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between"
                                    >
                                        <div className="min-w-0 space-y-0.5">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-sm font-semibold text-foreground">{entry.name}</span>
                                                <span className={cn(badgeClass, 'border-border text-muted-foreground')}>
                                                    {planStateLabel(t, entry)}
                                                </span>
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                {entry.awaitingPayment
                                                    ? seatsLabel(t, entry.purchased)
                                                    : t('SEATS_IN_USE_OF', {
                                                          held: entry.held,
                                                          total: entry.held + entry.freeThisPeriod,
                                                          defaultValue: '{{held}} of {{total}} seats in use',
                                                      })}
                                                {price && ` · ${price}`}
                                                {!entry.awaitingPayment && entry.periodEndUtc &&
                                                    ` · ${
                                                        entry.cancelScheduled
                                                            ? t('ENDS_ON', { date: formatDate(entry.periodEndUtc), defaultValue: 'ends {{date}}' })
                                                            : t('RENEWS_ON', { date: formatDate(entry.periodEndUtc), defaultValue: 'renews {{date}}' })
                                                    }`}
                                            </p>
                                            {note && <p className="text-xs text-muted-foreground">{note}</p>}
                                        </div>
                                        <PlanActions entry={entry} />
                                    </div>
                                );
                            })}
                        </div>
                        <Separator className="h-px bg-border" />
                        <BillingDetailsSection />
                    </>
                )}
            </div>
        </div>
    );
};
